// Submenu — Main controller
//
// Manages the open/close state of a submenu via [is-open] attribute.
// Handles scroll-lock on iPhone to prevent background scrolling.
//
// Events emitted:
//   submenu:opened  → panel positions itself (desktop)
//   submenu:close   → trigger syncs its [is-open] state
//
// Events consumed:
//   submenu:open         → toggle open/close
//   submenu:requestClose → close (from panel or drawer)
//
// Usage:
//   <sub-menu data-controller="submenu--index" data-submenu-id="my-menu"
//     data-action="submenu:open@window->submenu--index#toggle submenu:requestClose->submenu--index#close">

import { Controller } from "@hotwired/stimulus"

const MOBILE_BREAKPOINT = 1000
const CLOSE_DELAY = 1000

export default class extends Controller {
  connect() {
    this.submenuId = this.element.dataset.submenuId
    this.scrollTop = 0
    this.locked = false
  }

  disconnect() {
    if (this.locked) this.#unlockScroll()
  }

  toggle(event) {
    if (event.detail.id !== this.submenuId) return

    this.element.hasAttribute("is-open") ? this.close() : this.#open()
  }

  close() {
    if (!this.element.hasAttribute("is-open")) return

    this.#unlockScroll()
    this.element.removeAttribute("is-open")
    this.dispatch('close', {
      prefix: 'submenu',
      target: window,
      detail: { id: this.submenuId },
    })
  }

  closeWithDelay(event) {
    const delay = parseInt(event.currentTarget.dataset.delay) || CLOSE_DELAY
    setTimeout(() => this.close(), delay)
  }

  #open() {
    this.element.setAttribute("is-open", "true")
    this.#lockScroll()
    this.dispatch("opened", { prefix: "submenu", target: window, detail: { id: this.submenuId } })
  }

  #shouldLockScroll() {
    return document.body.classList.contains('iphone') && window.innerWidth < MOBILE_BREAKPOINT
  }

  #lockScroll() {
    this.locked = this.#shouldLockScroll()
    if (this.locked) {
      this.scrollTop = window.scrollY || document.documentElement.scrollTop || 0
      document.body.style.top = `-${this.scrollTop}px`
    }
    document.documentElement.classList.add('scroll-lock')
  }

  #unlockScroll() {
    if (this.locked) {
      document.body.style.top = ''
      requestAnimationFrame(() => window.scrollTo(0, this.scrollTop))
    }
    document.documentElement.classList.remove('scroll-lock')
    this.locked = false
  }
}
